import { useCallback, useEffect, useRef } from 'react';

/**
 * useHaptic — cross-platform soft haptic feedback for tap-driven UI.
 *
 * Two mechanisms combined, in order of preference:
 *
 *  1. **iOS Safari 17.4+ switch trick.** Toggling an `<input type="checkbox" switch>`
 *     inside a trusted click handler (i.e. one initiated by the user tapping
 *     a button) causes WebKit to fire the native "switch toggled" soft haptic.
 *     This is currently the only reliable way to trigger haptic feedback
 *     from web JS on iOS Safari — the Vibration API is not exposed there.
 *
 *  2. **Vibration API** (`navigator.vibrate`). Works on Android Chrome /
 *     Firefox and most Chromium-based mobile browsers. Silently no-ops on
 *     iOS and desktop.
 *
 * The hook lazily creates a single shared, offscreen switch input on the
 * document body — all component instances share it, so we never accumulate
 * dozens of hidden DOM nodes. Calling the returned `trigger()` function
 * from inside a user-initiated event handler is enough.
 *
 * Usage:
 *   const haptic = useHaptic();
 *   const onTap = () => { haptic(); doStuff(); };
 *
 * Optional intensity: 'light' | 'medium' | 'heavy' (Android only — iOS uses
 * the OS-defined switch haptic weight regardless).
 */
const SWITCH_ID = '__pd_haptic_switch';

const ensureSwitch = () => {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById(SWITCH_ID);
  if (el) return el;
  el = document.createElement('input');
  el.type = 'checkbox';
  el.id = SWITCH_ID;
  // The `switch` attribute (Safari 17.4+) turns the checkbox into a
  // native iOS-style toggle. WebKit fires a soft haptic when it toggles
  // inside a trusted click. Other browsers just see a hidden checkbox.
  el.setAttribute('switch', '');
  el.setAttribute('aria-hidden', 'true');
  el.tabIndex = -1;
  el.style.cssText =
    'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
  document.body.appendChild(el);
  return el;
};

export const useHaptic = () => {
  const switchRef = useRef(null);

  useEffect(() => {
    switchRef.current = ensureSwitch();
    // Intentionally do NOT remove the shared switch on unmount — other
    // consumers on the page may still need it, and it's just one node.
  }, []);

  const trigger = useCallback((intensity = 'light') => {
    // 1) iOS 17.4+ Safari — invoking .click() on the shared switch from
    //    within our button's onClick keeps us inside the trusted-event
    //    call stack, so WebKit fires the native soft haptic AND toggles
    //    the checkbox state atomically. On non-Safari browsers the click
    //    just flips a hidden checkbox — invisible, no side effects.
    const el = switchRef.current || ensureSwitch();
    if (el) {
      try { el.click(); } catch (_) { /* noop */ }
    }

    // 2) Vibration API — Android + most Chromium mobile. Short pulse so
    //    it feels like a tick, never like a phone ringer.
    if (
      typeof navigator !== 'undefined'
      && typeof navigator.vibrate === 'function'
    ) {
      const dur = intensity === 'heavy' ? 20 : intensity === 'medium' ? 12 : 8;
      try { navigator.vibrate(dur); } catch (_) { /* noop */ }
    }
  }, []);

  return trigger;
};

export default useHaptic;
